import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherProfileService } from '../services/api';
import TeacherSpotlightCard from './TeacherSpotlightCard';

const asList = (data) => Array.isArray(data) ? data : data?.results || [];

export default function FeaturedTeachersCarousel({ teachers: suppliedTeachers, title, subtitle, autoAdvance = true, onAvailabilityChange }) {
  const { t } = useTranslation();
  const [loadedTeachers, setLoadedTeachers] = useState([]);
  const [loading, setLoading] = useState(suppliedTeachers === undefined);
  const trackRef = useRef(null);
  const teachers = suppliedTeachers === undefined ? loadedTeachers : suppliedTeachers;

  useEffect(() => {
    if (suppliedTeachers !== undefined) return;
    teacherProfileService.list({ featured: true, page_size: 20 })
      .then((res) => {
        const list = asList(res.data);
        setLoadedTeachers(list);
        onAvailabilityChange?.(list.length > 0);
      })
      .catch(() => {
        setLoadedTeachers([]);
        onAvailabilityChange?.(false);
      })
      .finally(() => setLoading(false));
  }, [suppliedTeachers, onAvailabilityChange]);

  const move = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    const amount = Math.max(280, track.clientWidth * 0.82) * direction;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
    const atStart = track.scrollLeft <= 8;
    if ((direction > 0 && atEnd) || (direction < 0 && atStart)) {
      track.scrollTo({ left: direction > 0 ? 0 : track.scrollWidth, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!autoAdvance || teachers.length < 2) return undefined;
    const timer = window.setInterval(() => move(1), 4500);
    return () => window.clearInterval(timer);
    // teachers.length intentionally restarts the timer when data arrives
  }, [autoAdvance, teachers.length]);

  if (!loading && teachers.length === 0) return null;

  return (
    <div>
      <div className="mb-9 text-center">
        <div className="mx-auto max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">{t('Learn from the best')}</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-gray-900">{title || t('Valuable teachers')}</h2>
          {subtitle && <p className="mt-3 text-gray-600">{subtitle}</p>}
        </div>
        {teachers.length > 3 && (
          <div className="mt-5 hidden justify-center gap-2 sm:flex">
            <button type="button" onClick={() => move(-1)} aria-label={t('Previous teachers')} className="flex h-10 w-10 items-center justify-center rounded-full border border-surface-200 bg-white text-gray-600 shadow-sm transition hover:border-primary-200 hover:text-primary-600">←</button>
            <button type="button" onClick={() => move(1)} aria-label={t('Next teachers')} className="flex h-10 w-10 items-center justify-center rounded-full border border-surface-200 bg-white text-gray-600 shadow-sm transition hover:border-primary-200 hover:text-primary-600">→</button>
          </div>
        )}
      </div>
      {loading ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-white/70" />)}
        </div>
      ) : (
        <div ref={trackRef} className={`flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${teachers.length <= 3 ? 'lg:justify-center' : ''}`}>
          {teachers.map((teacher) => (
            <div key={teacher.id} className="w-[84%] shrink-0 snap-start sm:w-[47%] lg:w-[31.7%]">
              <TeacherSpotlightCard teacher={teacher} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
